import React from "react";
import { useCopies } from "shared/hooks/useCopies";
import { useStaticData } from "context/staticData";
import { PurchaseOrderDetailsPropTypes } from "../types";
import { getPurchaseOrderLineAmountsForDetails } from "../utils";
import { getEntityTaxDisplayLabel, getFormattedCurrency } from "shared/utils";
import PurchaseOrderTableVertical from "./components/purchaseOrderTableVertical";

function PurchaseOrderTable(props: PurchaseOrderDetailsPropTypes) {
  const copies = useCopies();
  const staticDataObj = useStaticData();
  const hasQuantity = React.useMemo(
    () => Boolean(props.purchaseOrder?.lines?.reduce((acc, currLine) => acc + currLine.quantity, 0)),
    [props.purchaseOrder?.lines]
  );

  const hasCouponDiscount = React.useMemo(
    () => props.purchaseOrder?.lines?.some(line => line.discountForUser?.discountAmount),
    [props.purchaseOrder?.lines]
  );

  if (props.isMobile) return <PurchaseOrderTableVertical {...props} />;

  return (
    <div className="purchaseOrderTable">
      {props.title && <h3 className="purchaseOrderTable_title">{props.title}</h3>}
      <div className="purchaseOrderTable_wrapper">
        <table className="purchaseOrderTable_table" cellPadding="0" cellSpacing="0">
          <thead className="purchaseOrderTable_thead">
            <tr className="purchaseOrderTable_thead--row">
              <th className="purchaseOrderTable_thead--th">{copies.get("item")}</th>
              <th className="purchaseOrderTable_thead--th">{hasQuantity && copies.get("quantity.short")}</th>
              <th className="purchaseOrderTable_thead--th">{copies.get("price")}</th>
              <th className="purchaseOrderTable_thead--th">{copies.get("tax")}</th>
              {hasCouponDiscount && (
                <th className="purchaseOrderTable_thead--th">{copies.get("coupon.discount.label")}</th>
              )}
              <th className="purchaseOrderTable_thead--th">{copies.get("total")}</th>
            </tr>
          </thead>
          <tbody className="purchaseOrderTable_tbody">
            {props.purchaseOrder?.lines?.map(item => {
              const poLineAmounts = getPurchaseOrderLineAmountsForDetails(item);
              return (
                <tr key={item.purchaseOrderLineId} className="purchaseOrderTable_tbody--row">
                  <td className="purchaseOrderTable_tbody--td">
                    <p className="purchaseOrderTable--lineType">{staticDataObj.inventoryType[item.lineType]}</p>
                    {props.originatingFormName && (
                      <p className="purchaseOrderTable--formName">{props.originatingFormName}</p>
                    )}
                    {item.couponCode && (
                      <p className="buyInventory_coupon">
                        <span className="buyInventory_coupon--text">{copies.get("coupon.code.applied.label")}:</span>
                        <span title={item.couponCode} className="buyInventory_coupon--value">
                          {item.couponCode}
                        </span>
                      </p>
                    )}
                  </td>
                  <td className="purchaseOrderTable_tbody--td">{hasQuantity && item.quantity} </td>
                  <td className="purchaseOrderTable_tbody--td">{poLineAmounts.price}</td>
                  <td className="purchaseOrderTable_tbody--td">
                    <p className="purchaseOrderTable_taxDetails">
                      {poLineAmounts.tax}
                      <small className="purchaseOrderTable_taxDetails--taxInfo">
                        {getEntityTaxDisplayLabel(copies, "line.item.tax.info", item.taxConfiguration)}
                      </small>
                    </p>
                  </td>
                  {poLineAmounts.discount && (
                    <td className="purchaseOrderTable_tbody--td">
                      <span className="discountedPrice">- {poLineAmounts.discount}</span>
                    </td>
                  )}
                  <td className="purchaseOrderTable_tbody--td">{getFormattedCurrency(item.totalAmount)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="purchaseOrderTable_tfoot">
            <tr className="purchaseOrderTable_tfootTotalAmount">
              <td className="purchaseOrderTable_tfootTotalAmount--text" colSpan={hasCouponDiscount ? 5 : 4}>
                {props.customFooter && props.customFooter}
                {copies.get("total")}:
              </td>
              <td className="purchaseOrderTable_tfootTotalAmount--value">
                {getFormattedCurrency(props.purchaseOrder?.amount)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default PurchaseOrderTable;
